import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowLeft, ChevronDown, Mail, Search, Trash2, UserPlus } from 'lucide-react';

import { Button } from '@ui/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@ui/components/ui/dialog';
import { Input } from '@ui/components/ui/input';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@ui/components/ui/input-group';
import { MultiSelectField } from '@ui/components/ui/multi-select';

import { useOrganizationId } from '@app/utils/hooks';
import {
  ClientAccessRole,
  ClientCompanyPerson,
  UserEntity,
  useClientColleagues,
  useClientProducts,
  useInviteClientColleague,
  useMyClientCompanies,
  useRemoveClientAccess,
  useUpdateClientAccess,
} from '@data';
import { reasonOf } from '@data/utils/errors';

const displayName = (person: Pick<ClientCompanyPerson, 'firstName' | 'lastName' | 'email'>): string =>
  `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim() || person.email;

/*
 * appearance-none with an overlaid chevron, so the arrow sits the same distance from the corner as the
 * MultiSelectField's rather than at the browser's default inset — every dropdown in the panel lines up.
 */
const selectClass =
  'h-8 w-full appearance-none rounded-lg border border-input bg-popover pl-2.5 pr-8 text-sm text-foreground outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50';

const ChevronSelect = ({
  className,
  children,
  ...props
}: React.ComponentProps<'select'> & { className?: string }): ReactElement => (
  <div className={cx('relative', className)}>
    <select className={selectClass} {...props}>
      {children}
    </select>
    <ChevronDown
      aria-hidden
      className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
    />
  </div>
);

const sectionLabel = 'mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground';

/**
 * Who at this client reads its reports, and what each of them may do.
 *
 * Managing any of it is an admin's here — a view-only colleague sees the same list and no controls, and
 * the API refuses them anyway. The rules that would otherwise lock a client out of its own reports are
 * enforced there too: nobody manages themselves, a super admin is changed only by another, and the last
 * admin can be neither demoted nor removed.
 */
const InviteColleaguesDialog = ({
  open,
  onOpenChange,
  user,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserEntity;
}): ReactElement => {
  const { t } = useTranslation();
  const organizationId = useOrganizationId() ?? '';

  const { data: companyData } = useMyClientCompanies(organizationId, open);
  const companies = companyData?.items ?? [];
  const [companyId, setCompanyId] = useState<string | undefined>();
  // Settles on the first client once they arrive; somebody working with one never sees the picker.
  useEffect(() => {
    if (!companyId && companies.length) setCompanyId(companies[0].id);
  }, [companies, companyId]);

  const { data: colleagueData } = useClientColleagues(organizationId, companyId, open);
  const { data: productData } = useClientProducts(organizationId);
  const invite = useInviteClientColleague(organizationId);
  const changeAccess = useUpdateClientAccess(organizationId);
  const removeAccess = useRemoveClientAccess(organizationId);

  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<ClientAccessRole>(ClientAccessRole.read);
  const [projects, setProjects] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Whose row is mid-save, so a second tick cannot be made before the first one has landed.
  const [changing, setChanging] = useState<string | null>(null);

  const people = colleagueData?.items ?? [];
  // Found by address, which is what the list carries: it decides whether this screen offers controls.
  const me = people.find(person => person.email.toLowerCase() === (user.email ?? '').toLowerCase());
  const iAmAdmin = me?.role === ClientAccessRole.admin;

  const options = (productData?.items ?? [])
    .map(product => ({ value: product.id, label: product.name ?? '' }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const summaryFor = (total: number) => (count: number) =>
    count === total ? t('allProducts') : t('access.nProducts', { count });
  const idsFor = (person: ClientCompanyPerson): string[] =>
    options.filter(option => person.products.includes(option.label)).map(option => option.value);

  const matching = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return people;
    return people.filter(person => `${displayName(person)} ${person.email}`.toLowerCase().includes(needle));
  }, [people, query]);
  const shownAdmins = matching.filter(person => person.role === ClientAccessRole.admin);
  const shownViewers = matching.filter(person => person.role === ClientAccessRole.read);
  const noMatches = Boolean(query.trim()) && !matching.length;

  // The fade under the list, shown while there is more to scroll to.
  const listRef = useRef<HTMLDivElement>(null);
  const [atListEnd, setAtListEnd] = useState(true);
  const measureList = (): void => {
    const element = listRef.current;
    if (element) setAtListEnd(element.scrollHeight - element.scrollTop - element.clientHeight < 4);
  };
  useEffect(() => {
    if (open) measureList();
  }, [open, matching.length, adding]);

  const resetForm = (): void => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setRole(ClientAccessRole.read);
    // Everything, because a colleague is brought in to read the client's work: the form opens on the
    // common case, and taking a product off it is the deliberate act.
    setProjects(options.map(option => option.value));
    setError(null);
  };

  const alreadyHere = people.some(person => person.email.toLowerCase() === email.trim().toLowerCase());
  const canInvite = Boolean(firstName.trim() && email.trim()) && !alreadyHere && !invite.isPending;

  const send = async (): Promise<void> => {
    setError(null);
    try {
      await invite.mutateAsync({
        clientCompanyId: companyId,
        firstName: firstName.trim(),
        lastName: lastName.trim() || undefined,
        email: email.trim(),
        productIds: projects,
        role,
      });
      toast.success(t('access.invitationSent', { email: email.trim() }));
      resetForm();
      setAdding(false);
    } catch (failure) {
      const reason = reasonOf(failure);
      setError(reason === 'alreadyMember' ? t('access.duplicatePerson') : t('access.addPersonFailed'));
    }
  };

  // The two refusals worth naming: both are rules a reader can act on, unlike anything else going wrong.
  const refusals: Record<string, string> = {
    lastClientAdmin: t('access.lastAdmin'),
    superAdminProtected: t('access.superAdminProtected'),
  };

  const change = async (person: ClientCompanyPerson, values: { role?: ClientAccessRole; productIds?: string[] }) => {
    setChanging(person.membershipId);
    try {
      await changeAccess.mutateAsync({ clientCompanyId: companyId!, membershipId: person.membershipId, ...values });
    } catch (failure) {
      const reason = reasonOf(failure);
      toast.error(refusals[reason] ?? t('access.changeFailed'));
    } finally {
      setChanging(null);
    }
  };

  const remove = async (person: ClientCompanyPerson) => {
    try {
      await removeAccess.mutateAsync({ clientCompanyId: companyId!, membershipId: person.membershipId });
    } catch (failure) {
      const reason = reasonOf(failure);
      toast.error(refusals[reason] ?? t('access.changeFailed'));
    }
  };

  const renderPerson = (person: ClientCompanyPerson): ReactElement => {
    const name = displayName(person);
    // A protected admin, and yourself, are shown rather than offered: the API refuses both, and a
    // control that cannot succeed is worse than none.
    const editable = iAmAdmin && !person.isSuperAdmin && person.membershipId !== me?.membershipId;
    const scope = person.allProducts ? t('allProducts') : t('access.nProducts', { count: person.products.length });

    return (
      <div
        key={person.membershipId}
        className="flex items-center gap-3 border-b border-border py-2.5 duration-200 ease-out animate-in fade-in-0 last:border-b-0"
      >
        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
          <Mail className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground">{name}</p>
          {name !== person.email && <p className="truncate text-xs text-muted-foreground">{person.email}</p>}
        </div>
        {editable ? (
          <div className="flex items-center gap-3">
            {/* Disabled while a change is in flight, because each tick is a save of the whole list: two
                of them at once are two saves of two different lists, and the later one loses the earlier
                one's tick. The row is unreadable for the moment it takes, which is the honest state. */}
            <MultiSelectField
              label={<span className="sr-only">{t('a11y.productsPersonCanView', { email: person.email })}</span>}
              options={options}
              value={idsFor(person)}
              onChange={ids => change(person, { productIds: ids })}
              allSelected={person.allProducts}
              onToggleAll={all => change(person, { productIds: all ? options.map(option => option.value) : [] })}
              emptyLabel={t('noProducts')}
              summaryThreshold={0}
              renderCountSummary={count => (person.allProducts ? t('allProducts') : t('access.nProducts', { count }))}
              selectAllLabel={t('allProducts')}
              disabled={changing === person.membershipId}
              className="w-36 shrink-0 gap-0"
            />
            <ChevronSelect
              aria-label={t('a11y.roleFor', { email: person.email })}
              className="w-36 shrink-0"
              value={person.role}
              onChange={event => change(person, { role: event.target.value as ClientAccessRole })}
            >
              <option value={ClientAccessRole.admin}>{t('access.admin')}</option>
              <option value={ClientAccessRole.read}>{t('access.viewOnly')}</option>
            </ChevronSelect>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => remove(person)}
              aria-label={`${t('actions.remove')} ${person.email}`}
              className="shrink-0 text-muted-foreground hover:text-foreground"
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <span className="shrink-0 text-right text-sm text-muted-foreground">{scope}</span>
            {person.isSuperAdmin && (
              <span className="shrink-0 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
                {t('access.superAdmin')}
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderGroup = (title: string, list: ClientCompanyPerson[]): ReactElement | null =>
    list.length ? (
      <section>
        <p className={sectionLabel}>{title}</p>
        <div>{list.map(renderPerson)}</div>
      </section>
    ) : null;

  // Only worth showing when there is a choice to make. Outside the list/invite branch, because the
  // client it names is both what is listed and where an invitation lands.
  const companySwitcher =
    companies.length > 1 ? (
      <div className="-mt-1 flex items-center gap-2">
        <label htmlFor="access-company" className="shrink-0 text-sm font-medium text-foreground">
          {t('access.company')}
        </label>
        {/* Sized to the field rather than the dialog: stretched full width it reads as the dialog's main
            control, which it is not. */}
        <ChevronSelect
          id="access-company"
          className="min-w-0 max-w-[14rem] flex-1"
          value={companyId ?? ''}
          onChange={event => setCompanyId(event.target.value)}
        >
          {companies.map(company => (
            <option key={company.id} value={company.id}>
              {company.name}
            </option>
          ))}
        </ChevronSelect>
      </div>
    ) : null;

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        onOpenChange(next);
        if (!next) {
          resetForm();
          setAdding(false);
          setQuery('');
        }
      }}
    >
      {/* Wide enough for what a row actually holds: a name and address beside two pickers and a delete.
          At xl the pickers left about a hundred points for the address, so every one of them truncated. */}
      <DialogContent className="max-h-[85vh] sm:max-w-2xl">
        <DialogHeader>
          {adding ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setAdding(false)}
                aria-label={t('back')}
                className="-ml-1 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <ArrowLeft className="size-4" />
              </button>
              <DialogTitle>{t('access.inviteColleague')}</DialogTitle>
            </div>
          ) : (
            // pr-8 keeps the button clear of the dialog's own close control.
            <div className="flex items-center justify-between gap-3 pr-8">
              <DialogTitle>{t('access.peopleWithAccess')}</DialogTitle>
              {iAmAdmin && (
                <Button
                  size="sm"
                  onClick={() => {
                    resetForm();
                    setAdding(true);
                  }}
                >
                  <UserPlus />
                  {t('titles.invite')}
                </Button>
              )}
            </div>
          )}
        </DialogHeader>

        {companySwitcher}

        {adding ? (
          <form
            onSubmit={event => {
              event.preventDefault();
              send().catch(() => undefined);
            }}
            className="mt-1 space-y-3 duration-200 animate-in fade-in-0"
          >
            <div className="grid grid-cols-2 gap-3">
              <Input
                value={firstName}
                onChange={event => setFirstName(event.target.value)}
                placeholder={t('firstName')}
              />
              <Input value={lastName} onChange={event => setLastName(event.target.value)} placeholder={t('lastName')} />
            </div>
            <div>
              <Input
                type="email"
                value={email}
                onChange={event => setEmail(event.target.value)}
                placeholder="name@example.com"
                aria-invalid={Boolean(error) || alreadyHere}
              />
              {alreadyHere && <p className="mt-1 text-sm text-destructive">{t('access.duplicatePerson')}</p>}
              {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
            </div>

            <div className="flex flex-wrap items-end gap-3">
              <div className="flex flex-col gap-1 text-sm font-medium text-foreground">
                {t('role')}
                <ChevronSelect
                  aria-label={t('role')}
                  className="w-36"
                  value={role}
                  onChange={event => setRole(event.target.value as ClientAccessRole)}
                >
                  <option value={ClientAccessRole.admin}>{t('access.admin')}</option>
                  <option value={ClientAccessRole.read}>{t('access.viewOnly')}</option>
                </ChevronSelect>
              </div>
              <div className="flex flex-1 flex-col gap-1 text-sm font-medium text-foreground">
                {t('access.canView')}
                <MultiSelectField
                  label={<span className="sr-only">{t('a11y.productsNewPersonCanView')}</span>}
                  options={options}
                  value={projects}
                  onChange={setProjects}
                  emptyLabel={t('noProducts')}
                  summaryThreshold={0}
                  renderCountSummary={summaryFor(options.length)}
                  selectAllLabel={t('allProducts')}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAdding(false)}>
                {t('actions.cancel')}
              </Button>
              <Button type="submit" disabled={!canInvite}>
                <UserPlus />
                {t('access.sendInvite')}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          // min-w-0 because the dialog is a grid, and a grid item defaults to min-width:auto — it will
          // not shrink below its content. A row of people carries two fixed-width pickers and a delete,
          // so once somebody was added the list pushed itself wider than the dialog and the right-hand
          // controls, the super-admin pill and the close button all ran off the edge.
          <div className="min-w-0 duration-200 animate-in fade-in-0">
            <p className="-mt-1 text-sm text-muted-foreground">{t('access.inviteHint')}</p>

            <div className="mt-2">
              <InputGroup className="bg-popover">
                <InputGroupAddon>
                  <Search />
                </InputGroupAddon>
                <InputGroupInput
                  placeholder={t('access.searchPeople')}
                  value={query}
                  onChange={event => setQuery(event.target.value)}
                  aria-label={t('access.searchPeople')}
                />
              </InputGroup>
            </div>

            <div className="relative mt-5">
              <div ref={listRef} onScroll={measureList} className="max-h-96 space-y-5 overflow-y-auto">
                {renderGroup(t('access.admins'), shownAdmins)}
                {renderGroup(t('access.viewOnly'), shownViewers)}
                {noMatches && (
                  <p className="py-6 text-center text-sm text-muted-foreground">
                    {t('access.noPeopleMatch', { query })}
                  </p>
                )}
              </div>
              <div
                aria-hidden
                className={cx(
                  'pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-popover to-transparent transition-opacity duration-200',
                  atListEnd && 'opacity-0',
                )}
              />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InviteColleaguesDialog;
